// © Microsoft Corporation. All rights reserved.

import { AppEnvironment, appEnvironment } from "../app/AppEnvironment";
import { IRequestContext, IRequestData } from "./IRequestContext";
import { IRequestDataInteropValue, IRequestDataSpecification, RequestData } from "@msnews/ssr-server";
import { SSRContract, SSRRequestDataPrefix } from "@msnews/isomorphic-util";

/**
 * Defines the parameters used to create a request contract.
 * Destructured from an object due to the combinations of parameters that are valid.
 */
export interface IRequestContextContract {
    /**
     * URL Search params that contain request data (client-side values sent to the server).
     * Only required when called server-side
     */
    params?: URLSearchParams;

    /**
     * The prefix to use to pull query params related to request data.
     */
    qspPrefix?: string;

    /**
     * The specification defining what request data should be passed between client and server.
     */
    spec?: IRequestDataSpecification;
}

/**
 * Used to propagate client-only data (like window.innerWidth) and singletons (like AppEnvironment)
 * in a non-global way to assist in making all libs/components/experiences isomorphic.
 */
export class RequestContext implements IRequestContext {
    private requestData: RequestData<IRequestData>;
    private RequestId: string;

    /**
     * Create a new request context.
     *
     * If spec or qspPrefix is not provided it will use the defaults specified in "@msnews/isomorphic-util"
     *
     * @param IRequestContextContract parameters used to initialize the request context
     */
    constructor({ params, qspPrefix, spec }: IRequestContextContract = {}, requestId?: string) {
        this.requestData = new RequestData(spec || SSRContract, qspPrefix || SSRRequestDataPrefix);
        if (params) {
            this.requestData.loadQueryParams(params);
        }
        if (requestId) {
            this.RequestId = requestId;
        }
    }

    /**
     * The request ID is used to distinguish requests server-side.
     * This method passes through the app environment ActivityId
     */
    public get requestId(): string {
        return this.RequestId || this.app.ActivityId;
    }

    /**
     * The current app environment.
     */
    public get app(): AppEnvironment {
        // TODO: Remove global reference and add to the constructor parameters.
        // The constructor should create a new app environment or accept an optional override.
        return appEnvironment;
    }

    /**
     * The current request data. i.e. client-side data that is passed
     * between client and server.
     */
    public get data(): IRequestData {
        return this.requestData.data;
    }
}

/**
 * Get clientValue from data getter.
 * @param dataGetter the data getter to get client-side value from spec
 * @param dataName the data name
 * @param throwException If true, throws an exception
 */
export function getContextData(dataGetter: any, dataName: string, throwException = true) {
    if ((typeof(dataGetter) === "function")) {
        const value = dataGetter();
        if (value || value === 0) {
            return value;
        }
    }
    if (throwException) {
        throw new Error("Expected " + dataName + " to return value. Possible invalid RequestContext spec used or the spec doesn't contain this data.");
    }
    return undefined;
}

/**
 * Get current URL from request context. in browser, it has the same value with window.location.href
 * @param context the request context
 */
export function safeGetLocationHref(context: IRequestContext): string {
    return getContextData(context.data.locationHref, "locationHref", false);
}